// i18n/supportedLanguages.ts — canonical list of supported narration
// languages for Phase A i18n.
//
// MIRROR of server/src/services/supportedLanguages.ts. The shape is shared
// (same data, on both sides for simplicity); when a code is added or
// removed, update both files in the same commit. The server validates
// inbound language codes against SUPPORTED_CODES; the client renders the
// user-facing picker from SUPPORTED_LANGUAGES (native names).
//
// Why BCP-47 with regional variants only where they're truly distinct:
//   - "zh-Hans" / "zh-Hant" — the script difference matters for narration.
//   - Single-tag codes ("fr", "es", "de") cover the rest. We don't ship
//     "fr-CA" vs "fr-FR" yet; Phase A treats those as the same code. The
//     resolveSupportedLanguage helper strips region tags from
//     navigator.languages output to fall back to the bare code.

export interface SupportedLanguage {
  /** BCP-47 code, e.g. "fr" or "zh-Hans". */
  code: string;
  /** English display name, e.g. "French". Used by admin / debug surfaces. */
  name: string;
  /** Native display name, e.g. "Français". Used by the user-facing picker. */
  nativeName: string;
}

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "pl", name: "Polish", nativeName: "Polski" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
  { code: "zh-Hans", name: "Chinese (Simplified)", nativeName: "简体中文" },
  { code: "zh-Hant", name: "Chinese (Traditional)", nativeName: "繁體中文" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe" },
  { code: "sv", name: "Swedish", nativeName: "Svenska" },
];

export const SUPPORTED_CODES: Set<string> = new Set(
  SUPPORTED_LANGUAGES.map((l) => l.code),
);

/**
 * Resolve a browser locale tag (e.g. "fr-FR", "zh-Hant-TW") to a supported
 * canonical code. Falls back to the bare language subtag when the exact
 * regional variant isn't supported — "fr-FR" → "fr", "es-MX" → "es",
 * "en-AU" → "en". Returns null when nothing matches.
 *
 * Mirror of the server-side function with the same name.
 */
export function resolveSupportedLanguage(locale: string): string | null {
  if (typeof locale !== "string" || locale.length === 0) return null;
  const lower = locale.toLowerCase();

  // Exact match (after normalising the canonical entries to lowercase for
  // comparison). Iterate so we can return the canonical-cased code.
  for (const entry of SUPPORTED_LANGUAGES) {
    if (entry.code.toLowerCase() === lower) return entry.code;
  }

  // Chinese script disambiguation. The browser sends region tags ("zh-CN",
  // "zh-TW") rather than script tags, but the script is what we care about
  // for narration.
  if (lower === "zh" || lower.startsWith("zh-cn") || lower.startsWith("zh-sg")) {
    return "zh-Hans";
  }
  if (lower.startsWith("zh-tw") || lower.startsWith("zh-hk") || lower.startsWith("zh-mo")) {
    return "zh-Hant";
  }
  if (lower.startsWith("zh-hans")) return "zh-Hans";
  if (lower.startsWith("zh-hant")) return "zh-Hant";

  // Strip the region tag and retry as a bare language subtag.
  const dash = lower.indexOf("-");
  if (dash > 0) {
    const base = lower.slice(0, dash);
    for (const entry of SUPPORTED_LANGUAGES) {
      if (entry.code.toLowerCase() === base) return entry.code;
    }
  }

  return null;
}

/**
 * Look up a SupportedLanguage entry by code. Returns undefined when the
 * code isn't in the list.
 */
export function findSupportedLanguage(
  code: string,
): SupportedLanguage | undefined {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code);
}
